import { ClientConfigurationModel, IBlobSasModel, IPhotoRequest } from "@common/models";
import { BlobEntityType, HospitalPrescriptionStatus, MimeType } from "@common/models/Enums";
import { HospitalPrescriptionCancelModel } from "@common/models/HospitalPrescriptionCancelModel";
import { HospitalPrescriptionModel } from "@common/models/HospitalPrescriptionModel";
import { RXServiceDispensingHistoryItemModel } from "@common/models/RXServiceDispensingHistoryItemModel";
import { RxSKUItemModel } from "@common/models/RxSKUItemModel";
import { DispensingHistoryRequest } from "@common/models/requests/DispensingHistoryRequest";
import { HospitalService, PrescriptionService } from "@common/services";
import { AzureBlobStore, NotificationStoreInstance } from "@common/stores";
import { QueryClient } from "@tanstack/react-query";
import { flow, makeObservable, observable } from "mobx";
import { v4 as uuid } from 'uuid';

export type HospitalPrescriptionStoreInit = {
    configuration?: ClientConfigurationModel,
    queryClient?: QueryClient
}

export class HospitalPrescriptionStore {
    @observable hospitalPrescriptions: HospitalPrescriptionModel[] | undefined;
    @observable hospitalPrescription: HospitalPrescriptionModel | undefined;
    @observable rxSKUItem: RxSKUItemModel[] | undefined;
    @observable dispensingHistory: RXServiceDispensingHistoryItemModel[] = [];
    @observable dispensingServiceUnavailable: boolean | undefined;
    configuration: ClientConfigurationModel | undefined;
    queryClient: QueryClient | undefined;

    constructor(init?: HospitalPrescriptionStoreInit) {
        this.configuration = init?.configuration;
        this.queryClient = init?.queryClient;
        makeObservable(this, undefined, { autoBind: true });
    }

    /**
     * Adds or update a collection of Hospital Prescriptions
     * @param this current HospitalPrescriptionStore reference
     * @param hospitalPrescriptions array of HospitalPrescription models to create/update
     */
    upsertHospitalPrescriptions = flow(function* (this: HospitalPrescriptionStore, hospitalId: number, hospitalPrescriptions: HospitalPrescriptionModel[]) {
        this.hospitalPrescriptions = yield HospitalService.upsertHospitalPrescriptions(hospitalId, hospitalPrescriptions)
            .then(NotificationStoreInstance.onSuccess(`Hospital Prescriptions ${hospitalPrescriptions[0].hospitalPrescriptionStatusId === HospitalPrescriptionStatus.Cancelled ? 'canceled' : 'updated'}`))
            .catch(NotificationStoreInstance.onError("Failed to update Hospital Prescription", hospitalPrescriptions));
    });

    cancelHospitalPrescription = flow(function* (this: HospitalPrescriptionStore, hospitalPrescriptionId: number, cancelData: HospitalPrescriptionCancelModel) {
        return yield HospitalService.cancelHospitalPrescription(hospitalPrescriptionId, cancelData)
            .then(NotificationStoreInstance.onSuccess("Hospital Prescriptions canceled"))
            .catch(NotificationStoreInstance.onError("Failed to cancel Hospital Prescription", cancelData));
    });

    public savePrescriptionSignature = flow(function* (this: HospitalPrescriptionStore, image: string) {
        return yield this.uploadSignature(image, `signaturePhoto-prescription-${uuid()}.png`);
    });

    private uploadSignature = flow(function* (this: HospitalPrescriptionStore, image: string, filename: string) {
        const blobStore = new AzureBlobStore({ containerName: BlobEntityType.Signatures });
        const blobSas: IBlobSasModel = yield blobStore.uploadDataUrl(image, filename, MimeType.PNG);
        const photoRequest: IPhotoRequest = {
            container: blobSas.container,
            fileName: blobSas.fileName,
            photoUrl: blobSas.sasUrl
        };

        return photoRequest;
    });

    public getHospitalPrescription = flow(function* (this: HospitalPrescriptionStore, hospitalPrescriptionId: number) {
        this.hospitalPrescription = yield PrescriptionService.getHospitalPrescription(hospitalPrescriptionId);
    });

    public getRXDispensingHistory = flow(function* (this: HospitalPrescriptionStore, request: DispensingHistoryRequest) {
        this.dispensingHistory = yield PrescriptionService.getRXDispensingHistory(request)
            .catch(() => { this.dispensingServiceUnavailable = true });
    });

    public getRXSKUQuantities = flow(function* (this: HospitalPrescriptionStore) {
        if (!this.queryClient) {
            this.rxSKUItem = yield PrescriptionService.getRXSKUQuantities();
            return;
        }

        this.rxSKUItem = yield this.queryClient.fetchQuery({
            queryKey: [PrescriptionService.tags.getRXSKUQuantities],
            queryFn: () => PrescriptionService.getRXSKUQuantities(),
            staleTime: this.configuration?.longClientSideCacheTimeToLive ?? 28800000
        });
    });

    public recallHospitalPrescription = flow(function* (this: HospitalPrescriptionStore, hospitalPrescriptionId: number) {
        return yield PrescriptionService.recallHospitalPrescription(hospitalPrescriptionId)
            .then(NotificationStoreInstance.onSuccess("Hospital Prescriptions recalled"))
            .catch(NotificationStoreInstance.onError("Failed to recall Hospital Prescription", {}));
    });

    public transferHospitalPrescription = flow(function* (this: HospitalPrescriptionStore, hospitalPrescriptionId: number, note: string) {
        return yield PrescriptionService.transferHospitalPrescription(hospitalPrescriptionId, note)
            .then(NotificationStoreInstance.onSuccess("Hospital Prescriptions transfered"))
            .catch(NotificationStoreInstance.onError("Failed to transfer Hospital Prescription", {}));
    });
}
